import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, LogOut, MessageSquare, Lightbulb, Gamepad2, Camera, Download, RefreshCw, Send, Bell, Image as ImageIcon, Upload, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'mbk_admin_token';

// ── tiny stat pill ──────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    className={`group relative text-left rounded-2xl border p-5 transition-all ${
      active
        ? 'border-violet-500/60 bg-violet-500/10'
        : 'border-white/[0.07] bg-white/[0.025] hover:border-white/[0.15] hover:bg-white/[0.05]'
    }`}
  >
    <div className="flex items-center justify-between mb-3">
      <Icon size={18} className={active ? 'text-violet-300' : 'text-gray-500 group-hover:text-white'} />
      {active && <span className="w-1.5 h-1.5 rounded-full bg-violet-400 shadow-[0_0_8px_rgba(168,85,247,0.9)]" />}
    </div>
    <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{value}</div>
    <div className="text-[10px] text-gray-500 tracking-widest uppercase mt-1">{label}</div>
  </button>
);

// ── Login screen ────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.access_token);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#080611] flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle, rgba(124,58,237,0.08) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
      }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(124,58,237,0.18) 0%, transparent 65%)' }} />

      <form onSubmit={submit} data-testid="admin-login-form"
        className="relative z-10 w-[380px] rounded-2xl border border-white/[0.08] bg-[#0c0918]/80 backdrop-blur-xl p-7"
        style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(139,92,246,0.1)' }}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center">
            <Lock size={16} className="text-violet-300" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Admin</h1>
            <p className="text-[10px] text-gray-500 tracking-widest uppercase">Restricted access</p>
          </div>
        </div>

        <label className="block text-[10px] text-gray-500 tracking-widest uppercase mb-1.5">Username</label>
        <input
          type="text" value={username} onChange={e => setUsername(e.target.value)}
          data-testid="admin-username-input"
          autoComplete="username"
          className="w-full mb-4 px-3.5 py-2.5 rounded-lg bg-black/40 border border-white/[0.08] text-white text-sm focus:outline-none focus:border-violet-500/60 transition-colors" />

        <label className="block text-[10px] text-gray-500 tracking-widest uppercase mb-1.5">Password</label>
        <input
          type="password" value={password} onChange={e => setPassword(e.target.value)}
          data-testid="admin-password-input"
          autoComplete="current-password"
          className="w-full mb-5 px-3.5 py-2.5 rounded-lg bg-black/40 border border-white/[0.08] text-white text-sm focus:outline-none focus:border-violet-500/60 transition-colors" />

        {err && (
          <div data-testid="admin-login-error" className="mb-4 text-[11px] text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
            {err}
          </div>
        )}

        <button
          type="submit" disabled={loading}
          data-testid="admin-login-submit"
          className="w-full font-bold py-3 rounded-lg text-sm tracking-wide transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            fontFamily: 'Syne, sans-serif',
            background: 'linear-gradient(135deg, #7c3aed, #a855f7 50%, #d946ef)',
            boxShadow: '0 4px 24px rgba(124,58,237,0.35)',
          }}>
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

// ── Section renderers ───────────────────────────────────────────────────────
const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

function AnswersTable({ items }) {
  if (!items.length) return <Empty label="No visitor messages yet" />;
  return (
    <div className="space-y-2" data-testid="answers-list">
      {items.map(a => (
        <div key={a.id} className="rounded-xl border border-white/[0.06] bg-white/[0.025] p-4 hover:bg-white/[0.04] transition-colors">
          <p className="text-sm text-white whitespace-pre-wrap break-words">{a.content}</p>
          <p className="text-[10px] text-gray-600 mt-2 tracking-wider uppercase">{fmt(a.created_date)}</p>
        </div>
      ))}
    </div>
  );
}

function SuggestionsTable({ items }) {
  if (!items.length) return <Empty label="No journal suggestions yet" />;
  return (
    <div className="space-y-2" data-testid="suggestions-list">
      {items.map(s => (
        <div key={s.id} className="rounded-xl border border-white/[0.06] bg-white/[0.025] p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] text-violet-300 bg-violet-500/10 border border-violet-500/20 rounded-full px-2.5 py-0.5 tracking-wider">
              {s.nickname || 'anon'}
            </span>
            <span className="text-[10px] text-gray-600 tracking-wider uppercase">{fmt(s.created_date)}</span>
          </div>
          <p className="text-sm text-white whitespace-pre-wrap break-words">{s.text}</p>
        </div>
      ))}
    </div>
  );
}

function HangmanTable({ items }) {
  if (!items.length) return <Empty label="No hangman plays yet" />;
  const resultColor = (r) => ({
    won: 'text-green-400 bg-green-500/10 border-green-500/20',
    lost: 'text-rose-400 bg-rose-500/10 border-rose-500/20',
    accessed: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
  }[r] || 'text-gray-400 bg-gray-500/10 border-gray-500/20');
  return (
    <div className="rounded-xl border border-white/[0.06] overflow-hidden" data-testid="hangman-list">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.04] text-[10px] text-gray-500 tracking-widest uppercase">
          <tr>
            <th className="text-left px-4 py-2.5">Nickname</th>
            <th className="text-left px-4 py-2.5">Result</th>
            <th className="text-left px-4 py-2.5">Wrong</th>
            <th className="text-left px-4 py-2.5">At</th>
          </tr>
        </thead>
        <tbody>
          {items.map(p => (
            <tr key={p.id} className="border-t border-white/[0.05] hover:bg-white/[0.02]">
              <td className="px-4 py-2.5 text-white font-medium">{p.nickname}</td>
              <td className="px-4 py-2.5">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border tracking-widest uppercase ${resultColor(p.result)}`}>
                  {p.result}
                </span>
              </td>
              <td className="px-4 py-2.5 text-gray-400">{p.wrong_guesses}</td>
              <td className="px-4 py-2.5 text-gray-500 text-[11px]">{fmt(p.created_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CapturesGrid({ items }) {
  if (!items.length) return <Empty label="No studio captures yet" />;
  const download = (url, filter) => {
    const a = document.createElement('a');
    a.href = url; a.download = `mbk-${filter}-${Date.now()}.png`; a.click();
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="captures-grid">
      {items.map(c => (
        <div key={c.id} className="rounded-xl overflow-hidden border border-white/[0.06] bg-black/30 group">
          <img src={c.image_data} alt="capture" className="w-full aspect-video object-cover cursor-pointer" onClick={() => window.open(c.image_data, '_blank')} />
          <div className="flex items-center justify-between px-3 py-2.5">
            <div>
              <p className="text-[10px] text-violet-300 font-semibold tracking-widest uppercase">{c.filter_type}</p>
              <p className="text-[10px] text-gray-600 mt-0.5">{fmt(c.created_date)}</p>
            </div>
            <button
              onClick={() => download(c.image_data, c.filter_type)}
              data-testid={`capture-download-${c.id}`}
              className="text-[10px] font-bold px-3 py-1 rounded-lg bg-violet-600/80 hover:bg-violet-500 text-white transition-colors flex items-center gap-1">
              <Download size={11} /> Save
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

const Empty = ({ label }) => (
  <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] py-16 flex items-center justify-center text-xs text-gray-600 tracking-widest uppercase">
    {label}
  </div>
);

// ── Push broadcast panel ────────────────────────────────────────────────────
function BroadcastPanel({ token, subscribers }) {
  const [title, setTitle] = useState('Islamic Reminders updated');
  const [body, setBody]   = useState("i just added something new — tap to read.");
  const [url, setUrl]     = useState('/current-lore');
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true); setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/push/broadcast`,
        { title, body, url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult({ ok: true, ...data });
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally { setSending(false); }
  };

  return (
    <div className="mb-8 rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/[0.06] to-fuchsia-500/[0.03] p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-violet-500/15 border border-violet-500/25 flex items-center justify-center">
            <Bell size={14} className="text-violet-300" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Push subscribers</h3>
            <p className="text-[10px] text-gray-500 tracking-widest uppercase">broadcast a notification</p>
          </div>
        </div>
        <span className="text-[10px] font-bold text-violet-300 bg-violet-500/10 border border-violet-500/20 rounded-full px-2.5 py-0.5">
          {subscribers} {subscribers === 1 ? 'subscriber' : 'subscribers'}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 mb-2">
        <input
          value={title} onChange={e => setTitle(e.target.value)}
          placeholder="Title"
          data-testid="broadcast-title"
          className="px-3 py-2 rounded-lg bg-black/40 border border-white/[0.08] text-white text-sm focus:outline-none focus:border-violet-500/60" />
        <input
          value={body} onChange={e => setBody(e.target.value)}
          placeholder="Body"
          data-testid="broadcast-body"
          className="px-3 py-2 rounded-lg bg-black/40 border border-white/[0.08] text-white text-sm focus:outline-none focus:border-violet-500/60" />
        <button
          onClick={send}
          disabled={sending || subscribers === 0 || !title || !body}
          data-testid="broadcast-send"
          className="px-4 py-2 rounded-lg font-bold text-xs tracking-wider transition-all flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            fontFamily: 'Syne, sans-serif',
            background: 'linear-gradient(135deg, #7c3aed, #a855f7 50%, #d946ef)',
            boxShadow: '0 4px 16px rgba(124,58,237,0.3)',
          }}>
          <Send size={11} /> {sending ? 'sending…' : 'send'}
        </button>
      </div>
      <input
        value={url} onChange={e => setUrl(e.target.value)}
        placeholder="Click-through URL (e.g. /current-lore)"
        data-testid="broadcast-url"
        className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/[0.08] text-gray-400 text-xs font-mono focus:outline-none focus:border-violet-500/60" />

      {result && (
        <p className={`mt-3 text-[11px] ${result.ok ? 'text-emerald-400' : 'text-rose-400'}`}>
          {result.ok
            ? `sent to ${result.sent} · ${result.failed} failed · ${result.removed_dead} removed`
            : `error: ${result.msg}`}
        </p>
      )}
    </div>
  );
}

// ── Archives manager (admin uploads old pictures) ───────────────────────────
function ArchivesPanel({ token, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [caption, setCaption] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [err, setErr] = useState('');

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/archives`, auth);
      setItems(data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load archives');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) { setErr('Please choose image files.'); return; }
    setErr(''); setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      if (caption.trim()) fd.append('caption', caption.trim());
      await axios.post(`${API}/admin/archives`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setCaption('');
      await load();
      onChange?.();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); setProgress(0); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this picture? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/archives/${id}`, auth);
      setItems(prev => prev.filter(i => i.id !== id));
      onChange?.();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="archives-panel" className="space-y-5">
      {/* Drop / pick zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
        className={`rounded-2xl border-2 border-dashed transition-colors p-6 ${
          dragOver ? 'border-violet-400/80 bg-violet-500/10' : 'border-white/[0.1] bg-white/[0.02]'
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <label
            data-testid="archives-upload-label"
            className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold tracking-wider text-white"
            style={{
              fontFamily: 'Syne, sans-serif',
              background: 'linear-gradient(135deg, #7c3aed, #a855f7 50%, #d946ef)',
              boxShadow: '0 4px 16px rgba(124,58,237,0.3)',
            }}
          >
            <Upload size={12} /> {uploading ? `uploading… ${progress}%` : 'choose pictures'}
            <input
              type="file" multiple accept="image/*" className="hidden"
              disabled={uploading}
              data-testid="archives-file-input"
              onChange={(e) => upload(e.target.files)}
            />
          </label>

          <input
            value={caption} onChange={(e) => setCaption(e.target.value)}
            placeholder="optional caption (applied to this batch)"
            data-testid="archives-caption-input"
            className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/[0.08] text-white text-sm focus:outline-none focus:border-violet-500/60" />
        </div>

        <p className="text-[11px] text-gray-500 mt-3">
          drag & drop multiple files here, or use the button. supports jpg, png, gif, webp, heic.
        </p>

        {uploading && (
          <div className="mt-3 h-1 bg-white/[0.05] rounded-full overflow-hidden">
            <div
              data-testid="archives-upload-progress"
              className="h-full bg-violet-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {err && (
          <p data-testid="archives-error" className="mt-3 text-[11px] text-rose-400">{err}</p>
        )}
      </div>

      {/* Existing archives grid */}
      {loading ? (
        <Empty label="Loading…" />
      ) : items.length === 0 ? (
        <Empty label="No archive pictures yet" />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="archives-admin-grid">
          {items.map(it => (
            <div key={it.id} className="rounded-xl overflow-hidden border border-white/[0.06] bg-black/30 group relative">
              <img
                src={it.image_data || `${BACKEND_URL}${it.url || ''}`}
                alt={it.caption || 'archive'}
                className="w-full aspect-square object-cover"
                loading="lazy"
              />
              <button
                onClick={() => remove(it.id)}
                data-testid={`archive-delete-${it.id}`}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-rose-500/80 hover:bg-rose-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                title="Delete"
              >
                <Trash2 size={13} />
              </button>
              <div className="px-3 py-2">
                <p className="text-[11px] text-white truncate" title={it.caption || ''}>
                  {it.caption || '—'}
                </p>
                <p className="text-[10px] text-gray-600 mt-0.5">{fmt(it.uploaded_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Dashboard ───────────────────────────────────────────────────────────────
function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState('answers');
  const [stats, setStats] = useState({ answers: 0, suggestions: 0, hangman_players: 0, captures: 0, archives: 0 });
  const [data, setData] = useState({ answers: [], suggestions: [], hangman_players: [], captures: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, a, su, h, c] = await Promise.all([
        axios.get(`${API}/admin/stats`, auth),
        axios.get(`${API}/admin/answers`, auth),
        axios.get(`${API}/admin/suggestions`, auth),
        axios.get(`${API}/admin/hangman-players`, auth),
        axios.get(`${API}/admin/captures`, auth),
      ]);
      setStats(s.data);
      setData({ answers: a.data, suggestions: su.data, hangman_players: h.data, captures: c.data });
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const tabs = [
    { id: 'answers',    label: 'Visitor Messages',    icon: MessageSquare, count: stats.answers },
    { id: 'suggestions',label: 'Journal Suggestions', icon: Lightbulb,     count: stats.suggestions },
    { id: 'hangman_players', label: 'Hangman Players',icon: Gamepad2,      count: stats.hangman_players },
    { id: 'captures',   label: 'Studio Captures',     icon: Camera,        count: stats.captures },
    { id: 'archives',   label: 'Archives',            icon: ImageIcon,     count: stats.archives || 0 },
  ];

  const renderTab = () => {
    if (tab === 'answers')          return <AnswersTable    items={data.answers} />;
    if (tab === 'suggestions')      return <SuggestionsTable items={data.suggestions} />;
    if (tab === 'hangman_players')  return <HangmanTable    items={data.hangman_players} />;
    if (tab === 'captures')         return <CapturesGrid    items={data.captures} />;
    if (tab === 'archives')         return <ArchivesPanel   token={token} onChange={fetchAll} />;
    return null;
  };

  return (
    <div className="min-h-screen bg-[#080611] text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>mbk admin</h1>
            <p className="text-[11px] text-gray-600 tracking-widest uppercase mt-0.5">All collected data · live</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchAll}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="px-3.5 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] text-xs text-gray-300 tracking-wider transition-colors flex items-center gap-1.5 disabled:opacity-50">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button
              onClick={() => { onLogout(); navigate('/admin'); }}
              data-testid="admin-logout-btn"
              className="px-3.5 py-2 rounded-lg bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 text-xs text-rose-300 tracking-wider transition-colors flex items-center gap-1.5">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>

        {/* Stat cards (also act as tab switchers) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
          {tabs.map(t => (
            <StatCard key={t.id}
              icon={t.icon} label={t.label} value={t.count}
              active={tab === t.id} onClick={() => setTab(t.id)} />
          ))}
        </div>

        {/* Push broadcast */}
        <BroadcastPanel token={token} subscribers={stats.push_subscribers || 0} />

        {/* Section */}
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-xs font-semibold text-gray-500 tracking-widest uppercase">
            {tabs.find(t => t.id === tab)?.label}
          </h2>
          <span className="text-[10px] text-gray-600 tracking-widest uppercase">
            {tab === 'archives' ? `${stats.archives || 0} items` : `${data[tab]?.length || 0} ${data[tab]?.length === 1 ? 'item' : 'items'}`}
          </span>
        </div>

        {loading && tab !== 'archives' && !data[tab]?.length ? (
          <Empty label="Loading…" />
        ) : renderTab()}
      </div>
    </div>
  );
}

// ── Entry ───────────────────────────────────────────────────────────────────
export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  // Verify token on mount
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); });
  }, []);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); };

  if (!token) return <LoginScreen onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
